import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { fmt } from "@/lib/utils-msip";
import { DPC_STAGES, pctTone, heatBg, fmtPct, monthLabel, stageShort, STATUS_META } from "@/lib/dpc";
import { DpcControls, StageTiles, DealerPicker } from "@/pages/DpcCommand";
import { SalesTargetPanel } from "@/components/TargetPanel";
import { ProratedBadge } from "@/components/PlanBits";
import { TdFleetPanel } from "@/components/overview/TdFleet";
import { TIER_META } from "@/pages/DpcLeague";
import { Trophy, AlertTriangle, CheckCircle2, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarChart, Bar, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const TH = "px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground";

function PlanAchGrid({ rows, all, testId }) {
  return (
    <table className="w-full text-xs" data-testid={testId}>
      <thead className="border-b">
        <tr>
          <th className={`${TH} text-left`}>Model</th>
          {DPC_STAGES.map((s) => <th key={s} colSpan={3} className={`${TH} text-center border-l`}>{stageShort(s)}</th>)}
        </tr>
        <tr>
          <th className={TH}></th>
          {DPC_STAGES.map((s) => ["Plan", "Ach", "%"].map((h) => <th key={s + h} className={`${TH} text-right ${h === "Plan" ? "border-l" : ""}`}>{h}</th>))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.model} className="border-b last:border-0 hover:bg-muted/50">
            <td className="px-2 py-1 font-medium whitespace-nowrap">{r.model}</td>
            {DPC_STAGES.map((s) => {
              const c = r.cells[s];
              return [
                <td key={s + "p"} className="px-2 py-1 text-right num text-muted-foreground border-l">{c.plan || ""}</td>,
                <td key={s + "a"} className="px-2 py-1 text-right num font-semibold">{c.ach || ""}</td>,
                <td key={s + "%"} className={`px-2 py-1 text-right num ${pctTone(c.pct)}`}>{c.pct != null ? `${Math.round(c.pct)}` : ""}</td>,
              ];
            })}
          </tr>
        ))}
        <tr className="bg-secondary/70 font-bold border-t">
          <td className="px-2 py-1">ALL MODELS</td>
          {DPC_STAGES.map((s) => {
            const c = all[s];
            return [
              <td key={s + "p"} className="px-2 py-1 text-right num border-l">{c.plan}</td>,
              <td key={s + "a"} className="px-2 py-1 text-right num">{c.ach}</td>,
              <td key={s + "%"} className={`px-2 py-1 text-right num ${pctTone(c.pct)}`}>{c.pct != null ? `${Math.round(c.pct)}` : "—"}</td>,
            ];
          })}
        </tr>
      </tbody>
    </table>
  );
}

function TodayEntry({ dealerCode, models, onSaved, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [grid, setGrid] = useState({});
  const save = async () => {
    const rows = [];
    Object.entries(grid).forEach(([model, stages]) => Object.entries(stages).forEach(([stage, qty]) => {
      if (qty !== "" && qty != null) rows.push({ model, stage, qty: +qty });
    }));
    if (!rows.length) return toast.error("Enter at least one number");
    try {
      await api.post("/dpc/ach/entry", { date, dealer_code: dealerCode, rows });
      toast.success("Saved today's numbers");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="dpc-entry-form">
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44 text-sm" data-testid="dpc-entry-date" />
      <div className="overflow-auto border rounded-md">
        <table className="w-full text-xs">
          <thead className="border-b"><tr><th className={`${TH} text-left`}>Model</th>{DPC_STAGES.map((s) => <th key={s} className={`${TH} text-right`}>{stageShort(s)}</th>)}</tr></thead>
          <tbody>
            {models.map((m) => (
              <tr key={m} className="border-b last:border-0">
                <td className="px-2 py-1 font-medium whitespace-nowrap">{m}</td>
                {DPC_STAGES.map((s) => (
                  <td key={s} className="px-1 py-0.5">
                    <input type="number" min="0" className="w-16 h-7 border rounded px-1 text-right text-xs" data-testid={`entry-${m.replace(/\s/g, "-")}-${s.replace(/\s/g, "-")}`}
                      value={grid[m]?.[s] ?? ""} onChange={(e) => setGrid((g) => ({ ...g, [m]: { ...(g[m] || {}), [s]: e.target.value } }))} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button onClick={save} data-testid="dpc-entry-save">Save numbers</Button>
    </div>
  );
}

const FOCUS_META = {
  on_track: { cls: "border-emerald-300 bg-emerald-50", icon: <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5" /> },
  funnel_leak: { cls: "border-red-300 bg-red-50", icon: <AlertTriangle size={14} className="text-red-600 shrink-0 mt-0.5" /> },
  activation: { cls: "border-amber-300 bg-amber-50", icon: <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" /> },
  model_gap: { cls: "border-orange-300 bg-orange-50", icon: <Target size={14} className="text-orange-600 shrink-0 mt-0.5" /> },
  source_gap: { cls: "border-orange-300 bg-orange-50", icon: <Target size={14} className="text-orange-600 shrink-0 mt-0.5" /> },
  watch: { cls: "border-slate-300 bg-slate-50", icon: <AlertTriangle size={14} className="text-slate-500 shrink-0 mt-0.5" /> },
  no_plan: { cls: "border-slate-300 bg-slate-50", icon: <Target size={14} className="text-slate-500 shrink-0 mt-0.5" /> },
};

export default function DpcDealer() {
  const { code } = useParams();
  const { user } = useApp();
  const [meta, setMeta] = useState(null);
  const [ranks, setRanks] = useState(null);
  const [month, setMonth] = useState("");
  const [period, setPeriod] = useState("mtd");
  const [tab, setTab] = useState("week");
  const [data, setData] = useState(null);
  const [entryOpen, setEntryOpen] = useState(false);

  useEffect(() => {
    api.get("/dpc/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!data?.month) return;
    api.get(`/dpc/leaderboard?${qs({ month: month || data.month })}`).then(({ data }) => setRanks(data.boards)).catch(() => setRanks(null));
  }, [month, data?.month]);

  const load = useCallback(() => {
    api.get(`/dpc/dashboard/dealer/${code}?${qs({ month, period })}`).then(({ data }) => { setData(data); setMonth((m) => m || data.month); }).catch((e) => { toast.error(apiError(e)); setData(null); });
  }, [code, month, period]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-muted-foreground py-20 text-center">Loading dealer cockpit…</div>;
  const canEnter = user?.role === "admin" || (user?.role === "manager" && (user?.dealer_codes || []).includes(code?.toUpperCase()));
  const sm = STATUS_META[data.plan_status] || STATUS_META.not_started;
  const models = [...new Set(data.week_grid[0]?.rows.map((r) => r.model) || [])];
  const badges = ranks
    ? [["dpi", "DPI"], ["retail", "Retail"], ["conversion", "Conversion"]].map(([k, l]) => {
        const b = ranks.find((x) => x.key === k);
        const row = b?.rows.find((x) => x.dealer_code === data.dealer_code);
        return row?.rank ? { key: k, label: l, rank: row.rank, tier: row.tier } : null;
      }).filter(Boolean)
    : [];

  return (
    <div className="space-y-5" data-testid="dpc-dealer">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">{data.dealer_code} · {data.dealer_name}</h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1 flex-wrap">
            <span>{data.district}</span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${sm.cls}`} data-testid="dpc-plan-status">{sm.label}{data.plan_version > 1 ? ` · Plan v${data.plan_version}${data.revised_at ? ` (revised ${data.revised_at.slice(0, 10)})` : ""}` : ""}</span>
            <ProratedBadge count={data.prorated ? 1 : 0} testId="dpc-dealer-prorated" />
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px]">Upload streak: {data.compliance_streak}d</span>
            {badges.map((b) => {
              const tm = TIER_META[b.tier];
              return (
                <Link key={b.key} to="/dpc/league" className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-bold ${tm ? tm.cls : "bg-secondary text-muted-foreground"}`} data-testid={`dpc-badge-${b.key}`}>
                  <Trophy size={10} className={tm?.icon} />{b.label} #{b.rank}
                </Link>
              );
            })}
            <Link to={`/dpc/plan/${data.dealer_code}?month=${data.month}`} className="underline hover:text-foreground" data-testid="dpc-open-plan-link">Open plan</Link>
          </div>
        </div>
        <DpcControls month={month || data.month} setMonth={setMonth} period={period} setPeriod={setPeriod} extra={
          <>
            <DealerPicker meta={meta} value={data.dealer_code} />
            {canEnter && <Button size="sm" variant="outline" onClick={() => setEntryOpen(true)} data-testid="dpc-entry-open">Enter today's numbers</Button>}
          </>
        } />
      </div>

      <StageTiles tiles={data.tiles} />

      <SalesTargetPanel month={month || data.month} dealer={data.dealer_code} myCodes={[data.dealer_code]} />

      <TdFleetPanel f={data.td_fleet} month={data.month} />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border bg-card rounded-md p-4 h-fit" data-testid="dpc-focus-card">
          <h3 className="font-heading text-sm font-semibold mb-3">Focus areas — {monthLabel(data.month)}</h3>
          <div className="space-y-2">
            {(data.focus_areas || []).map((f, i) => {
              const fm = FOCUS_META[f.type] || FOCUS_META.watch;
              return (
                <div key={i} className={`border rounded-md p-3 flex items-start gap-2 ${fm.cls}`} data-testid={`dpc-focus-${f.type}`}>
                  {fm.icon}
                  <div>
                    <div className="text-xs font-bold">{f.title}{f.impact > 0 ? <span className="ml-1.5 font-normal text-muted-foreground">impact ~{f.impact} units</span> : null}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{f.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="border bg-card rounded-md p-4" data-testid="dpc-conversion-card">
          <h3 className="font-heading text-sm font-semibold mb-3">Conversion funnel — achieved vs planned</h3>
          <div className="space-y-3">
            {(data.conversions || []).map((c) => (
              <div key={c.step}>
                <div className="flex justify-between text-[11px] mb-1">
                  <span className="font-semibold">{c.step}</span>
                  <span className="num">
                    <span className={c.ach != null && c.plan != null ? (c.ach >= c.plan ? "text-emerald-700 font-bold" : "text-red-600 font-bold") : ""}>{c.ach != null ? `${c.ach}%` : "—"}</span>
                    <span className="text-muted-foreground"> vs plan {c.plan != null ? `${c.plan}%` : "—"}</span>
                  </span>
                </div>
                <div className="h-2.5 bg-secondary rounded-sm relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-foreground/15" style={{ width: `${Math.min(c.plan || 0, 130)}%` }} />
                  <div className="absolute inset-y-0 left-0 bg-[#BB162B]" style={{ width: `${Math.min(c.ach || 0, 130)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center rounded-md border overflow-hidden text-xs w-fit">
        {[{ v: "week", l: "Week-wise" }, { v: "source", l: "Source-wise" }, { v: "daily", l: "Daily trend" }].map(({ v, l }) => (
          <button key={v} data-testid={`dpc-tab-${v}`} onClick={() => setTab(v)}
            className={`px-3 py-1.5 font-medium transition-colors duration-200 ${tab === v ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`}>{l}</button>
        ))}
      </div>

      {tab === "week" && (
        <div className="space-y-4">
          {data.week_grid.map((w) => (
            <div key={w.week} className="border bg-card rounded-md overflow-auto">
              <div className="px-4 py-2.5 border-b flex items-center gap-2">
                <h3 className="font-heading text-sm font-bold">{w.week === "MONTH" ? "FULL MONTH" : w.week}</h3>
                <span className="text-xs text-muted-foreground">{monthLabel(data.month)} · days {w.start}–{w.end}</span>
              </div>
              <PlanAchGrid rows={w.rows} all={w.all} testId={`dpc-week-grid-${w.week}`} />
            </div>
          ))}
          {data.week_grid[0]?.rows.length === 0 && <p className="text-sm text-muted-foreground">No plan or achievement data yet for this month.</p>}
        </div>
      )}

      {tab === "source" && (
        <div className="space-y-4">
          {data.source_grid.map((s) => (
            <div key={s.source} className="border bg-card rounded-md overflow-auto">
              <div className="px-4 py-2.5 border-b flex items-baseline gap-2 flex-wrap">
                <h3 className="font-heading text-sm font-bold">{s.source}</h3>
                {(s.members || []).length > 1 && <span className="text-[11px] text-muted-foreground" data-testid={`dpc-source-members-${s.source.replace(/[^A-Z0-9]/gi, "-")}`}>= {s.members.join(" + ")}</span>}
              </div>
              <PlanAchGrid rows={s.rows} all={s.all} testId={`dpc-source-grid-${s.source.replace(/[^A-Z0-9]/gi, "-")}`} />
            </div>
          ))}
          {data.source_grid.length === 0 && <p className="text-sm text-muted-foreground">No source-wise data yet.</p>}
        </div>
      )}

      {tab === "daily" && (
        <div className="border bg-card rounded-md p-4" data-testid="dpc-daily-chart">
          <h3 className="font-heading text-sm font-semibold mb-3">Daily achievement vs plan — {monthLabel(data.month)}</h3>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={data.daily} margin={{ top: 10, right: 16, left: -18, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e5e9f0" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="retail" name="Retail" fill="#BB162B" />
              <Bar dataKey="booking" name="Booking" fill="#0d765a" />
              <Bar dataKey="test_drive" name="Test Drive" fill="#b8860b" />
              <Bar dataKey="enquiry" name="Enquiry" fill="#64748b" />
              <Line type="monotone" dataKey="cum_retail" name="Cum Retail" stroke="#BB162B" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="cum_plan" name="Plan to date" stroke="#0f172a" strokeDasharray="5 4" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
        <DialogContent className="max-w-3xl" data-testid="dpc-entry-dialog">
          <DialogHeader><DialogTitle className="font-heading">Enter daily numbers — {data.dealer_code}</DialogTitle></DialogHeader>
          <TodayEntry dealerCode={data.dealer_code} models={models.length ? models : []} onSaved={load} onClose={() => setEntryOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
